<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { closeModalById } from "@/utils/modal";
import { useEventTracking } from "@/hooks/useEventTracking";
import { useCurrentPowerActivityRights } from "@/hooks/useActivityRightsByType";
import Promotion618Countdown from "./Promotion618Countdown.vue";
import {
  PROMOTION_618_MODAL_HEIGHT,
  PROMOTION_618_MODAL_WIDTH
} from "./constants";

interface Props {
  modalId: string;
  imageUrl: string;
  popupName: "new_or_expired" | "active_member";
  countdownTop?: string;
  onClose?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  countdownTop: "47.2%"
});

const show = ref(false);
const hasClosed = ref(false);
const router = useRouter();

const { endTimestamp: activityEndTime } = useCurrentPowerActivityRights();

const shellStyle = computed(() => ({
  width: `${PROMOTION_618_MODAL_WIDTH}px`,
  height: `${PROMOTION_618_MODAL_HEIGHT}px`
}));

function trackClick(button: "dismiss" | "action") {
  useEventTracking({
    event: "click",
    eventValue: {
      button: button === "action" ? "promotion_618_action" : "promotion_618_dismiss",
      popupName: `popup-618-${props.popupName}`,
      routeName: router.currentRoute.value.name as string
    }
  });
}

function handleDismiss() {
  trackClick("dismiss");
  handleClose();
}

function handleAction() {
  trackClick("action");
  handleClose();
  router.push({ name: "power" });
}

function handleClose() {
  if (hasClosed.value) return;
  hasClosed.value = true;
  show.value = false;
  closeModalById(props.modalId);
  props.onClose?.();
}

onMounted(() => {
  show.value = true;
  useEventTracking({
    event: "popup",
    eventValue: {
      popupName: `popup-618-${props.popupName}`,
      routeName: router.currentRoute.value.name as string
    }
  });
});
</script>

<template>
  <a-modal
    v-model:open="show"
    wrap-class-name="promotion-618-modal important-popup"
    width="auto"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    :keyboard="false"
    :mask-style="{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }"
    centered
    :after-close="handleClose"
  >
    <div class="promotion-618-modal__shell" :style="shellStyle">
      <img class="promotion-618-modal__bg" :src="imageUrl" alt="618 促销活动" />

      <div class="promotion-618-modal__countdown" :style="{ top: countdownTop }">
        <Promotion618Countdown v-if="activityEndTime" :target-time="activityEndTime" />
      </div>

      <div class="promotion-618-modal__actions">
        <button type="button" class="promotion-618-modal__btn promotion-618-modal__btn--dismiss" @click="handleDismiss" />
        <button type="button" class="promotion-618-modal__btn promotion-618-modal__btn--action" @click="handleAction" />
      </div>
    </div>
  </a-modal>
</template>

<style lang="less">
.promotion-618-modal {
  .ant-modal-content {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  .ant-modal-body {
    padding: 0;
    line-height: 0;
  }
}
</style>

<style scoped lang="less">
.promotion-618-modal__shell {
  position: relative;
  overflow: hidden;
}

.promotion-618-modal__bg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.promotion-618-modal__countdown {
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;
}

.promotion-618-modal__actions {
  position: absolute;
  left: 50px;
  right: 50px;
  bottom: 24px;
  display: flex;
  gap: 8px;
  height: 64px;
  z-index: 3;
}

.promotion-618-modal__btn {
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;

  &--dismiss {
    flex: 0 0 38%;
  }

  &--action {
    flex: 1 1 auto;
  }
}
</style>
